import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Upload, Trash2, X, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { api, uploadUrl, formatApiErrorDetail } from "../lib/api";

const inputCls =
  "w-full bg-white border border-ink/15 px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-bronzo transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 mb-2";

const emptyProject = {
  title: "",
  location: "",
  year: "",
  category: "",
  description: "",
  materials: [],
  interventions: [],
  challenge: "",
  published: false,
  before_image: "",
  after_image: "",
};

const listToText = (arr) => (arr || []).join("\n");
const textToList = (t) => t.split("\n").map((s) => s.trim()).filter(Boolean);

export default function AdminPortfolio() {
  const [projects, setProjects] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [materialsText, setMaterialsText] = useState("");
  const [interventionsText, setInterventionsText] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  useEffect(() => load(), [load]);

  const startEdit = (p) => {
    setEditing(p);
    setForm({ ...emptyProject, ...p });
    setMaterialsText(listToText(p.materials));
    setInterventionsText(listToText(p.interventions));
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Il titolo è obbligatorio");
      return;
    }
    setSaving(true);
    const body = { ...form, materials: textToList(materialsText), interventions: textToList(interventionsText) };
    try {
      if (editing?.id) {
        await api.patch(`/admin/projects/${editing.id}`, body);
        toast.success("Progetto aggiornato");
      } else {
        const r = await api.post("/admin/projects", body);
        toast.success("Progetto creato — ora puoi caricare le foto");
        setEditing(r.data);
        setForm({ ...emptyProject, ...r.data });
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo progetto?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Progetto eliminato");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadImages = async (files) => {
    if (!editing?.id || !files.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      const r = await api.post(`/admin/projects/${editing.id}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing(r.data);
      setForm((f) => ({ ...f, images: r.data.images }));
      toast.success("Immagini caricate");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteImage = async (path) => {
    try {
      const r = await api.delete(`/admin/projects/${editing.id}/images`, { params: { path } });
      setEditing(r.data);
      setForm((f) => ({ ...f, images: r.data.images, before_image: r.data.before_image, after_image: r.data.after_image }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setFlag = async (field, value) => {
    const body = { ...form, materials: textToList(materialsText), interventions: textToList(interventionsText), [field]: value };
    try {
      const r = await api.patch(`/admin/projects/${editing.id}`, body);
      setEditing(r.data);
      setForm((f) => ({ ...f, [field]: value }));
      toast.success(field === "published" ? (value ? "Progetto pubblicato" : "Progetto nascosto") : "Immagine aggiornata");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (editing !== null) {
    return (
      <div data-testid="portfolio-editor">
        <Seo title="Modifica progetto | V32 Admin" noindex />
        <button
          onClick={() => { setEditing(null); load(); }}
          data-testid="editor-back"
          className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-bronzo transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" /> Tutti i progetti
        </button>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display font-light text-3xl text-ink">
            {editing.id ? form.title || "Progetto" : "Nuovo progetto"}
          </h1>
          {editing.id && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setFlag("published", !form.published)}
                data-testid="editor-publish-toggle"
                className={`inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 transition-colors duration-300 ${
                  form.published
                    ? "bg-green-700 text-white hover:bg-antracite"
                    : "bg-white border border-ink/20 text-ink hover:border-bronzo"
                }`}
              >
                {form.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                {form.published ? "Pubblicato" : "Non pubblicato"}
              </button>
              <button
                onClick={() => remove(editing.id)}
                data-testid="editor-delete"
                className="inline-flex items-center gap-2 text-sm text-red-700 border border-red-200 px-4 py-2.5 hover:bg-red-50 transition-colors duration-300"
              >
                <Trash2 className="w-4 h-4" /> Elimina
              </button>
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <label htmlFor="p-title" className={labelCls}>Nome del progetto *</label>
              <input id="p-title" data-testid="input-project-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Ristrutturazione bagno padronale" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="p-loc" className={labelCls}>Località</label>
                <input id="p-loc" data-testid="input-project-location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Es. Mendrisio" />
              </div>
              <div>
                <label htmlFor="p-year" className={labelCls}>Anno</label>
                <input id="p-year" data-testid="input-project-year" className={inputCls} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Es. 2026" />
              </div>
            </div>
            <div>
              <label htmlFor="p-cat" className={labelCls}>Tipologia di intervento</label>
              <input id="p-cat" data-testid="input-project-category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Es. Ristrutturazione completa" />
            </div>
            <div>
              <label htmlFor="p-desc" className={labelCls}>Descrizione</label>
              <textarea id="p-desc" data-testid="input-project-description" rows={5} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label htmlFor="p-challenge" className={labelCls}>Sfide tecniche e soluzioni (facoltativo)</label>
              <textarea id="p-challenge" data-testid="input-project-challenge" rows={3} className={inputCls} value={form.challenge} onChange={(e) => setForm({ ...form, challenge: e.target.value })} />
            </div>
          </div>
          <div className="space-y-5">
            <div>
              <label htmlFor="p-materials" className={labelCls}>Materiali principali (uno per riga)</label>
              <textarea id="p-materials" data-testid="input-project-materials" rows={5} className={inputCls} value={materialsText} onChange={(e) => setMaterialsText(e.target.value)} placeholder={"Gres porcellanato 60×120\nRubinetteria acciaio spazzolato"} />
            </div>
            <div>
              <label htmlFor="p-interv" className={labelCls}>Interventi eseguiti (uno per riga)</label>
              <textarea id="p-interv" data-testid="input-project-interventions" rows={5} className={inputCls} value={interventionsText} onChange={(e) => setInterventionsText(e.target.value)} placeholder={"Demolizione e smaltimento\nNuovo impianto idrico"} />
            </div>
            <button
              onClick={save}
              disabled={saving}
              data-testid="editor-save"
              className="w-full bg-antracite text-avorio font-medium py-3.5 hover:bg-bronzo disabled:opacity-50 transition-colors duration-300"
            >
              {saving ? "Salvataggio…" : editing.id ? "Salva modifiche" : "Crea progetto"}
            </button>
          </div>
        </div>

        {editing.id && (
          <div className="mt-10" data-testid="editor-images">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-medium text-xl text-ink">Fotografie</h2>
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="editor-upload-button"
                className="inline-flex items-center gap-2 bg-white border border-ink/20 text-sm px-4 py-2.5 hover:border-bronzo hover:text-bronzo transition-colors duration-300"
              >
                <Upload className="w-4 h-4" /> Carica immagini
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  uploadImages(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
            {(form.images || []).length === 0 ? (
              <p className="bg-white border border-ink/10 p-8 text-sm text-ink/50">
                Nessuna immagine. Carica le fotografie del cantiere: la prima sarà usata come copertina.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {form.images.map((img) => (
                  <div key={img} className="bg-white border border-ink/10" data-testid={`editor-image-${img.split("/").pop()}`}>
                    <img src={uploadUrl(img)} alt="Fotografia del progetto" className="w-full aspect-square object-cover" loading="lazy" />
                    <div className="p-2 flex flex-wrap gap-1">
                      <button
                        onClick={() => setFlag("before_image", img)}
                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${form.before_image === img ? "bg-bronzo text-white" : "bg-beige text-ink/60 hover:text-bronzo"}`}
                      >
                        Prima
                      </button>
                      <button
                        onClick={() => setFlag("after_image", img)}
                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${form.after_image === img ? "bg-bronzo text-white" : "bg-beige text-ink/60 hover:text-bronzo"}`}
                      >
                        Dopo
                      </button>
                      <button
                        onClick={() => deleteImage(img)}
                        aria-label="Elimina immagine"
                        className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-beige text-red-700 hover:bg-red-50 ml-auto"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs text-ink/40">
              Assegna un'immagine «Prima» e una «Dopo» per attivare il confronto interattivo nella scheda pubblica.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="admin-portfolio">
      <Seo title="Portfolio | V32 Admin" noindex />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-light text-3xl text-ink">Portfolio</h1>
          <p className="mt-2 text-sm text-ink/60">
            Solo i progetti marcati come «Pubblicato» compaiono sul sito. Carica esclusivamente lavori realmente eseguiti.
          </p>
        </div>
        <button
          onClick={() => startEdit(null)}
          data-testid="new-project-button"
          className="inline-flex items-center gap-2 bg-antracite text-avorio text-sm font-medium px-5 py-2.5 hover:bg-bronzo transition-colors duration-300"
        >
          <Plus className="w-4 h-4" /> Nuovo progetto
        </button>
      </div>

      <div className="mt-8">
        {projects === null ? (
          <div data-testid="portfolio-loading" />
        ) : projects.length === 0 ? (
          <p className="bg-white border border-ink/10 p-8 text-sm text-ink/50" data-testid="portfolio-empty">
            Nessun progetto ancora. Quando avrai documentato un cantiere completato, crealo qui: la sezione
            Realizzazioni del sito passerà automaticamente dalla galleria di ispirazione al portfolio reale.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => startEdit(p)}
                data-testid={`portfolio-item-${p.id}`}
                className="text-left bg-white border border-ink/10 hover:border-bronzo transition-colors duration-300 group"
              >
                <div className="aspect-[4/3] overflow-hidden bg-beige">
                  {p.images?.[0] ? (
                    <img src={uploadUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-ink/30 text-xs font-mono uppercase tracking-wider">
                      Nessuna foto
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-ink text-sm group-hover:text-bronzo transition-colors duration-300">{p.title}</p>
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 shrink-0 ${p.published ? "bg-green-100 text-green-800" : "bg-ink/10 text-ink/50"}`}>
                      {p.published ? "Pubblicato" : "Bozza"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-ink/50">{p.location}{p.year ? ` · ${p.year}` : ""}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
